import React, { useEffect, useState } from "react";
import { adminApi, formatApiError } from "../../lib/ledgerApi";
import { useBranding } from "../../contexts/BrandingContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Palette, Save } from "lucide-react";
import { toast } from "sonner";

export default function AdminBranding() {
  const { branding, refresh } = useBranding();
  const [form, setForm] = useState({
    product_name: "",
    brand_domain: "",
    primary_color: "#3B82F6",
    logo_url: "",
    favicon_url: "",
    support_email: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setForm({
      product_name: branding.product_name || "",
      brand_domain: branding.brand_domain || "",
      primary_color: branding.primary_color || "#3B82F6",
      logo_url: branding.logo_url || "",
      favicon_url: branding.favicon_url || "",
      support_email: branding.support_email || "",
    });
  }, [branding]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSave = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = Object.fromEntries(
        Object.entries(form).filter(([_, v]) => v !== "" && v !== null)
      );
      await adminApi.updateBranding(body);
      await refresh();
      toast.success("Branding saved", {
        description: "Applied across the app. Public pages will pick this up on next reload.",
      });
    } catch (err) {
      toast.error("Save failed", { description: formatApiError(err.response?.data?.detail) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl px-6 md:px-10 py-10 md:py-14">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">/ Super-admin · Branding</div>
        <h1 className="text-3xl sm:text-4xl tracking-tight font-medium flex items-center gap-3">
          <Palette className="w-6 h-6 text-muted-foreground" strokeWidth={1.5} />
          Branding
        </h1>
        <p className="mt-3 text-sm text-muted-foreground max-w-xl">
          Changes apply instantly to all users. If you change <code className="font-mono">brand_domain</code>,
          every business&apos;s VPA is re-keyed (e.g. <code className="font-mono">acme@neopay → acme@payx</code>).
        </p>
      </div>

      <form onSubmit={onSave} className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Product name</Label>
            <Input data-testid="branding-product-name" value={form.product_name} onChange={set("product_name")} className="rounded-sm mt-1" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Brand domain (used in VPAs)</Label>
            <Input data-testid="branding-brand-domain" value={form.brand_domain} onChange={set("brand_domain")} className="rounded-sm font-mono mt-1" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Primary color</Label>
            <div className="flex items-center gap-2 mt-1">
              <Input type="color" data-testid="branding-primary-color" value={form.primary_color} onChange={set("primary_color")} className="rounded-sm w-16 p-1 h-10" />
              <Input value={form.primary_color} onChange={set("primary_color")} className="rounded-sm font-mono" />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Support email</Label>
            <Input type="email" data-testid="branding-support-email" value={form.support_email} onChange={set("support_email")} className="rounded-sm font-mono mt-1" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Logo URL</Label>
            <Input data-testid="branding-logo-url" value={form.logo_url} onChange={set("logo_url")} className="rounded-sm font-mono mt-1" placeholder="https://..." />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Favicon URL</Label>
            <Input data-testid="branding-favicon-url" value={form.favicon_url} onChange={set("favicon_url")} className="rounded-sm font-mono mt-1" placeholder="https://..." />
          </div>
        </div>

        <div className="border border-border rounded-sm p-4 bg-card">
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Live preview</div>
          <div className="flex items-center gap-3">
            {form.logo_url ? (
              <img src={form.logo_url} alt="" className="w-10 h-10 object-contain rounded-sm border border-border" />
            ) : (
              <div className="w-10 h-10 inline-flex items-center justify-center rounded-sm border border-border" style={{ background: form.primary_color }}>
                <span className="text-[10px] font-bold text-white">{(form.product_name || "N").slice(0,1)}</span>
              </div>
            )}
            <div>
              <div className="text-lg font-medium">{form.product_name || "—"}</div>
              <div className="text-[11px] font-mono text-muted-foreground">acme@{form.brand_domain || "—"}</div>
            </div>
            <div className="ml-auto w-8 h-8 rounded-sm border border-border" style={{ background: form.primary_color }} />
          </div>
        </div>

        <Button type="submit" disabled={busy} data-testid="branding-save" className="rounded-sm">
          <Save className="w-4 h-4 mr-1" strokeWidth={1.5} /> {busy ? "Saving…" : "Save branding"}
        </Button>
      </form>
    </div>
  );
}
